// app/components/common/Radio/Radio.tsx
'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/app/lib';

const outer = cva(
  'h-[26px] w-[26px] rounded-full border-2 flex items-center justify-center transition-all group-hover:!bg-accent',
  {
    variants: {
      disabled: {
        true: 'opacity-50 cursor-not-allowed',
        false: 'cursor-pointer',
      },
      checked: { true: '', false: '' },
      tone: { default: '', greyout: '' },
    },
    compoundVariants: [
      { checked: true, tone: 'greyout', class: 'border-secondary-300' },
      {
        checked: true,
        tone: 'default',
        class: 'border-primary group-hover:border-primary-500',
      },
      { checked: false, class: 'border-secondary-300' },
    ],
    defaultVariants: { disabled: false, checked: false, tone: 'default' },
  },
);

const inner = cva('h-[18px] w-[18px] rounded-full transition-colors', {
  variants: {
    checked: { true: '', false: '' },
    tone: { default: '', greyout: '' },
  },
  compoundVariants: [
    { checked: true, tone: 'greyout', class: 'bg-secondary-300' },
    {
      checked: true,
      tone: 'default',
      class: 'bg-primary group-hover:bg-primary-500',
    },
    { checked: false, class: 'bg-transparent' },
  ],
  defaultVariants: { checked: false, tone: 'default' },
});

type TV = VariantProps<typeof outer>;

interface RadioProps {
  value: string;
  checked?: boolean;
  onChange?: (value: string) => void;
  status?: 'active' | 'disabled';
  variant?: 'default' | 'selected' | 'greyout';
  className?: string;
  outerClassName?: string;
  innerClassName?: string;
  label?: React.ReactNode;
  labelClassName?: string;
}

export function Radio({
  value,
  checked = false,
  onChange,
  status,
  variant,
  className,
  outerClassName,
  innerClassName,
  label,
  labelClassName,
}: RadioProps) {
  const isDisabled = status === 'disabled';
  const tone: NonNullable<TV['tone']> = variant === 'greyout' ? 'greyout' : 'default';

  const radioContent = (
    <>
      <input
        type="radio"
        checked={checked}
        onChange={() => !isDisabled && onChange?.(value)}
        disabled={isDisabled}
        className="sr-only"
      />
      <span className={cn(outer({ disabled: isDisabled, checked, tone }), outerClassName)}>
        <span className={cn(inner({ checked, tone }), innerClassName)} />
      </span>
    </>
  );

  return (
    <label
      className={cn(
        'group inline-flex items-center gap-2',
        isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        className,
      )}
    >
      {radioContent}
      {label !== undefined ? <span className={cn('text-sm leading-6', labelClassName)}>{label}</span> : null}
    </label>
  );
}
